import { pointOnCircle, type CircleLayout, type Point } from '@/app/components/divergenceGeometry';

/**
 * Arc maths for the instrument primitives.
 *
 * Kept beside the primitives rather than folded into divergenceGeometry, which
 * belongs to the hero ring and is already carrying that component's whole
 * coordinate system. These are the few extra pure functions the static marks
 * need, and nothing here touches the DOM, so every one of them runs on the
 * server.
 */

export const TAU = Math.PI * 2;

/** Angles are measured clockwise from twelve o'clock, because that is how a
 *  reader describes a dial. SVG measures from three o'clock, so every angle
 *  entering the geometry is rotated a quarter turn back. */
export function dialAngle(turns: number): number {
  return turns * TAU - TAU / 4;
}

/**
 * An SVG path for the arc between two angles, both in turns clockwise from
 * twelve o'clock.
 *
 * A full circle cannot be expressed as a single SVG arc — start and end would
 * be the same point and the renderer draws nothing — so a sweep at or beyond a
 * full turn is emitted as two half circles.
 */
export function arcPath(layout: CircleLayout, fromTurns: number, toTurns: number): string {
  if (!Number.isFinite(fromTurns) || !Number.isFinite(toTurns)) return '';
  const sweep = toTurns - fromTurns;
  if (sweep === 0) return '';

  const at = (turns: number) => pointOnCircle(layout, dialAngle(turns));
  const r = layout.radius;

  if (Math.abs(sweep) >= 1) {
    const a = at(fromTurns);
    const b = at(fromTurns + 0.5);
    return (
      `M ${a.x.toFixed(2)} ${a.y.toFixed(2)} ` +
      `A ${r} ${r} 0 0 1 ${b.x.toFixed(2)} ${b.y.toFixed(2)} ` +
      `A ${r} ${r} 0 0 1 ${a.x.toFixed(2)} ${a.y.toFixed(2)}`
    );
  }

  const start = at(fromTurns);
  const end = at(toTurns);
  const largeArc = Math.abs(sweep) > 0.5 ? 1 : 0;
  const clockwise = sweep > 0 ? 1 : 0;
  return (
    `M ${start.x.toFixed(2)} ${start.y.toFixed(2)} ` +
    `A ${r} ${r} 0 ${largeArc} ${clockwise} ${end.x.toFixed(2)} ${end.y.toFixed(2)}`
  );
}

/** Length of an arc, for a stroke-dash draw-in. */
export function arcLength(layout: CircleLayout, sweepTurns: number): number {
  return Math.abs(sweepTurns) * TAU * layout.radius;
}

/** Evenly spaced points along an arc — the tick marks on a dial. */
export function ticksAlong(
  layout: CircleLayout,
  fromTurns: number,
  toTurns: number,
  count: number,
  radiusOffset = 0
): Point[] {
  if (!Number.isInteger(count) || count < 2) return [];
  const step = (toTurns - fromTurns) / (count - 1);
  return Array.from({ length: count }, (_, i) =>
    pointOnCircle(layout, dialAngle(fromTurns + step * i), radiusOffset)
  );
}

/** Clamps a 0–1 reading so a bad score can never draw an arc past its track. */
export function clamp01(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.min(1, Math.max(0, value));
}
